/**
 * 단일 캐스트룸 녹화 제어 — admin WebSocket(/ws/admin)을 통해
 * cmd.recording.start / cmd.recording.stop 명령을 송신하고 상태 이벤트를 추적한다.
 *
 * 다중 캐스트룸 관리는 CastroomListComponent.vue가 별도로 수행하며, 이 composable은
 * /test/player, /test/caster 등 단일 캐스트룸을 다루는 페이지를 위한 단순화된 인터페이스다.
 */

import { ref, computed, onMounted, onUnmounted, type ComputedRef } from 'vue'
import { getStoredAuth } from '@/services/auth.credentials'
import type { CastroomId } from '@dronecast/shared'
import { useMonitoringStore } from '@/stores/monitoring.store'

const PROTOCOL = 'dronecast/1.0'
const RECONNECT_DELAY_MS = 5000
const ERROR_DISPLAY_MS = 5000

type RecordingStatus = 'starting' | 'recording' | 'stopping' | 'completed' | 'failed' | 'orphan'

interface UseRecordingControl {
  isRecording: ComputedRef<boolean>
  start(castroomId: CastroomId): void
  stop(): void
}

export function useRecordingControl(): UseRecordingControl {
  const monitoring = useMonitoringStore()

  const recordingId = ref<string | null>(null)
  const status = ref<RecordingStatus | null>(null)

  const isRecording = computed(() =>
    !!recordingId.value &&
    (status.value === 'recording' || status.value === 'starting' || status.value === 'stopping'),
  )

  let adminWs: WebSocket | null = null
  let unmounted = false

  function connect() {
    if (unmounted) return
    const wsProto = location.protocol === 'https:' ? 'wss' : 'ws'
    // 빌드타임 env 가 아니라 **로그인한 사용자**의 자격증명을 쓴다 — 안 그러면 빈 값으로 거부된다.
    const { userEmail, apiKey } = getStoredAuth()
    const url = `${wsProto}://${location.host}/ws/admin?userEmail=${encodeURIComponent(
      userEmail,
    )}&apiKey=${encodeURIComponent(apiKey)}`

    adminWs = new WebSocket(url)
    adminWs.onmessage = onMessage
    adminWs.onclose = () => {
      if (unmounted) return
      setTimeout(() => {
        if (adminWs?.readyState !== WebSocket.OPEN) connect()
      }, RECONNECT_DELAY_MS)
    }
  }

  function onMessage(event: MessageEvent) {
    let msg: any
    try {
      msg = JSON.parse(event.data)
    } catch {
      return
    }
    const { type, payload } = msg

    if (type === 'res.recording.start') {
      if (payload?.result === 'success' && payload.data) {
        recordingId.value = payload.data.recordingId
        status.value = 'recording'
      } else {
        flashError(`녹화 시작 실패: ${payload?.error || '알 수 없는 오류'}${payload?.errorCode ? ` (${payload.errorCode})` : ''}`)
        recordingId.value = null
        status.value = null
      }
    } else if (type === 'res.recording.stop') {
      if (payload?.result !== 'success') {
        flashError(`녹화 중지 실패: ${payload?.error || '알 수 없는 오류'}${payload?.errorCode ? ` (${payload.errorCode})` : ''}`)
      }
      // 성공 시는 evt.recording.completed에서 정리
    } else if (type === 'evt.recording.status_changed' && payload) {
      if (payload.recordingId === recordingId.value) {
        status.value = payload.status as RecordingStatus
      }
    } else if (type === 'evt.recording.completed' && payload?.recording) {
      if (payload.recording.recordingId === recordingId.value) {
        recordingId.value = null
        status.value = null
      }
    }
  }

  function flashError(message: string) {
    monitoring.setUserMessage(message)
    setTimeout(() => {
      if (monitoring.userMessage === message) monitoring.setUserMessage(null)
    }, ERROR_DISPLAY_MS)
  }

  function send(type: string, payload: any) {
    if (!adminWs || adminWs.readyState !== WebSocket.OPEN) {
      flashError('Admin WebSocket이 연결되지 않았습니다.')
      return
    }
    adminWs.send(JSON.stringify({ protocol: PROTOCOL, type, payload }))
  }

  function start(castroomId: CastroomId) {
    if (isRecording.value) return
    status.value = 'starting'
    send('cmd.recording.start', { castroomId })
  }

  function stop() {
    if (!recordingId.value) return
    status.value = 'stopping'
    send('cmd.recording.stop', { recordingId: recordingId.value })
  }

  onMounted(connect)
  onUnmounted(() => {
    unmounted = true
    if (adminWs) {
      adminWs.onclose = null
      adminWs.close()
      adminWs = null
    }
  })

  return { isRecording, start, stop }
}
